/**
 * VOD entity class
 */

import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'
import { has } from '../../../app/helpers/object'

/**
 * VODFactory
 * returns VOD entity class
 *
 * @class VODFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.VOD} class
 *
 * @ngdoc factory
 * @name mwApp.factory:VODFactory
 */
/* @ngInject */
export default function VODFactory ($http, $injector, Entity, VODSet, Network, Type, m2mMixin, onOffMixin, FileUploadMixin, DefaultStream, UrlPolicy, alertLiteSvc, authSvc) {
  /* === VOD class === */

  /**
   * VOD model
   *
   * @class VOD
   * @classdesc VOD model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.VOD} VOD entity
   */
  function VOD () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  VOD.prototype = Object.create(Entity.prototype)
  VOD.prototype.constructor = VOD

  /* === VOD instance methods & fields === */

  Object.assign(VOD.prototype, {

    /**
     * instance fields
     *
     * @memberof models.VOD#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      added: moment,
      ageRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      categories: ['Category'],
      description: String,
      directors: String,
      dolbyAtmosAvailable: Boolean,
      enabled: Boolean,
      imageUrl: String,
      imageHorizontalUrl: String,
      imageBackgroundUrl: String,
      length: String,
      locked: false,
      networks: Type.Stump(['Network'], ['id']),
      orderType: Type(String('Default')),
      resolution: 'HD',
      stars: String,
      subscriber: 'Subscriber',
      subscribers: ['Subscriber'],
      subscribersSize: Number,
      views: Number,
      year: Number,
      urls: {
        STB: 'MediaUrlDTO',
        PC: 'MediaUrlDTO',
        iOS: 'MediaUrlDTO',
        Android: 'MediaUrlDTO',
        SamsungTV: 'MediaUrlDTO',
        Others: 'MediaUrlDTO',
        WEB: 'MediaUrlDTO'
      },
      publishTime: String,
      unpublishTime: String,
      downloadable: Boolean,
      downloadFilename: String,
      downloadUrl: String,
      encoderSourceDownloadFilename: String,
      encoderSourceDownloadUrl: String,
      tags: [String],
      acl: {
        id: Number,
        name: String
      },
      tmdbId: Number,
      contentSets: ['VODSet'],
      assignedSubscriptions: ['Subscription'],
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreams'
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      partnerApp: {
        providerType: String,
        redirectUrl: String
      },
      /* local variables */
      $isSchedulerNotification: false,
      $scheduledPublishing: Boolean,
      $fromDate: Date,
      $fromTime: Date,
      $toDate: Date,
      $toTime: Date,
      $toDateUnlimited: true,
      $geoBlockingEnabled: false,
      $ageRatingType: 'default',
      $country: {
        id: Number,
        name: String,
        isoCode: String
      },
      $defaultAgeRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      $itemType: 'vod',
      $contentSetType: 'vodSet',
      $contentSetName: 'VOD Set',
      $partnerAppDeeplinkURL: Boolean
    },

    /**
     * url template
     *
     * @memberof models.VOD#
     * @type {String}
     */
    _urlTemplate: 'api/vods/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.VOD#
     * @type {String}
     */
    _publicUrlTemplate: 'vods/{{::id}}',

    /**
     * Prepare vod
     *
     * @memberof models.VOD#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      if (this.partnerApp) {
        this.$partnerAppDeeplinkURL = true
      }

      if (this.year === 0) {
        this.year = null
      }

      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      this.tags = this.tags.map(tag => {
        return { name: tag }
      })

      if (this.ageRating) {
        if (!this.ageRating.country) {
          this.$ageRatingType = 'default'
          this.$defaultAgeRating = this.ageRating
          this.ageRating = {}
        } else {
          this.$ageRatingType = 'countryBased'
          this.$country = this.ageRating.country
          this.$defaultAgeRating = {}
        }
      }
      this.$geoBlockingEnabled = !!this.acl

      /* Note: set default values. */
      this.$scheduledPublishing = !!this.publishTime
      this.$toDateUnlimited = !this.unpublishTime

      this.$fromDate = this.publishTime ? formatDateByTimezone(this.publishTime, false).toDate() : moment().toDate()
      this.$toDate = this.unpublishTime ? formatDateByTimezone(this.unpublishTime, false).toDate() : this.$fromDate
      this.$fromTime = timeStringToDate(this.publishTime ? moment.utc(this.publishTime).format('HH:mm') : null, 'startOfDay')
      this.$toTime = timeStringToDate(this.unpublishTime ? moment.utc(this.unpublishTime).format('HH:mm') : null, 'endOfDay')
    },

    /**
     * Get underlying object
     *
     * @memberof models.VOD#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      if (!this.$partnerAppDeeplinkURL) {
        this.partnerApp = null
      }

      const fromDate = this.$fromDate.setHours(this.$fromTime.getHours(), this.$fromTime.getMinutes(), 0)
      const toDate = this.$toDate.setHours(this.$toTime.getHours(), this.$toTime.getMinutes(), 0)
      this.publishTime = formatDateByTimezone(fromDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      this.unpublishTime = formatDateByTimezone(toDate).format('YYYY-MM-DDTHH:mm:ss[Z]')

      if (this.$toDateUnlimited) {
        this.unpublishTime = null
      }

      if (!this.$scheduledPublishing) {
        this.publishTime = null
        this.unpublishTime = null
      }
      this.acl = this.$geoBlockingEnabled ? this.acl : null

      this.ageRating = this.$ageRatingType === 'default' ? this.$defaultAgeRating : this.ageRating
      if (this.ageRating) {
        delete this.ageRating.country
      }

      // delete null urls
      if (this.urls) {
        this.urls =
          Object.keys(this.urls)
            .reduce(
              (o, platform) => {
                if (this.urls[platform] != null) {
                  o[platform] = this.urls[platform]
                }
                return o
              },
              Object.create(null)
            )
      }

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.VOD#
     * @param  {String} fieldName
     * @param  {String} size
     * @return {String}
     */
    _urlFor: function (fieldName, size) {
      return this[fieldName]
        ? this[fieldName].indexOf('{{size}}')
          ? this[fieldName].replace('{{size}}', size)
          : this[fieldName] + '?resize=' + size
        : ''
    },

    /**
     * Save devices settings
     *
     * @memberof models.VOD#
     * @param {Object} params - params to request
     * @returns {Promise}
     * @private
     */
    _saveDownloadableFileLink: function (params) {
      const base64EncodedResponseBody = window.btoa(JSON.stringify(params))
      return $http.post(this._url + '/uploaded', { base64EncodedResponseBody })
        .then((response) => response.data.payload)
    },

    /**
     * Save devices settings
     *
     * @memberof models.VOD#
     * @param {Object} params - params to request
     * @returns {Promise}
     * @private
     */
    _saveEncoderFileLink: function (params) {
      const base64EncodedResponseBody = window.btoa(JSON.stringify(params))
      return $http.post(this._url + '/encodersourceuploaded', { base64EncodedResponseBody })
        .then((response) => response.data.payload)
    }

  }, m2mMixin('networks'), m2mMixin('categories'), m2mMixin('tags'), onOffMixin, FileUploadMixin)

  /* === VOD static methods & fields === */

  /**
   * Move VOD to show
   *
   * @param vodId
   * @param params
   * @param params.tvShowId
   * @param params.seasonId
   * @param params.sortOrder
   * @param params.isWithDeleted
   * @return {Promise} promise
   */

  VOD.getLanguage = function () {
    return $http.get('api/tmdb/languages')
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  VOD.getVodFromTMDBById = function (id, params) {
    return $http.get(`api/tmdb/${id}`, { params })
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }
  VOD.getVodFromTMDBByLink = function (params) {
    return $http.get('api/tmdb', { params })
      .then((response) => response.data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  VOD.loadSmartFromDB = function (tableState) {
    var params = {
      q: tableState.params.title,
      language: tableState.params.language.isoCode,
      country: tableState.params.countryForAR.isoCode,
      adult: tableState.params.adult,
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) + 1 || 1,
      count: 20
    }

    return $http.get('api/tmdb', { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          // change SmartTable state
          if (Array.isArray(data.sort) && data.sort.length) {
            var last = data.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = (data.number - 1) * data.size // first element
          tableState.pagination.number = data.size // page size
          tableState.pagination.numberOfPages = data.totalPages
          tableState.pagination.totalItemCount = data.totalElements

          // return array for SmartTable
          return data
        }
      ).catch(alertLiteSvc.alertError)
  }

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  VOD.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Search tags by param
   *
   * @param {String} url
   * @param {Object} params
   * @returns {Promise} promise
   */
  VOD.search = function (url, params) {
    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get networks for content sets
   *
   * @param {Object} o
   * @param {Object} params
   * @returns {Promise} promise
   */
  VOD.loadNetworks = function (o, params) {
    return $http.get(VOD.generateUrl('/extra/content-sets/networks', o), { params })
      .data()
      .then((result) => result.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Load Content sets
   *
   * @param vod
   * @returns {*}
   */
  VOD.loadAvailableContentSets = function (vod) {
    return $http.get(`/nora/api/vods/${vod.id}/extra/content-sets`)
      .data()
      .then(data => data.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get vod sets by network id
   *
   * @param {Number} networkId
   * @param {Object} o
   * @param {Object} params
   * @returns {Promise} promise
   */
  VOD.loadVODSets = function (networkId, o, params) {
    return $http.get(VOD.generateUrl(`/extra/content-sets/networks/${networkId}/vod-sets`, o), { params })
      .data()
      .then((result) => result.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(VOD, Entity)
  VOD._name = 'VOD'
  VOD.init()

  // permissions
  VOD.access = {
    view: 'viewVod',
    create: 'createVod',
    edit: 'editVod',
    delete: 'deleteVod'
  }

  // extra
  VOD.extra = {
    acls: [{ id: Number, name: String }],
    categories: ['Category'],
    networks: ['Network'],
    playbackTokenGenerators: ['PlaybackTokenGenerator'],
    drmProfiles: ['DRMProfile'],
    uploadParameters: {
      uploadUrl: String,
      uploadFilenameParameter: String
    },
    encoderSourceUploadParameters: {
      uploadUrl: String,
      uploadFilenameParameter: String
    }
  }

  return VOD
}
